import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { theme } from "./styles/theme";
import { AuthProvider } from "./contexts/AuthContextWithQuery";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PageLoginPage } from "./pages/auth/LoginPage";
import { AppLayout } from "./components/layout/AppLayout";
import { PageDashboard } from "./pages/dashboard/Dashboard";
import { PageItemList } from "./pages/items/ItemList";
import { PageItemForm } from "./pages/items/ItemForm";
import { PageInvLotList } from "./pages/inventory/InvLotList";
import { PageInvLotForm } from "./pages/inventory/InvLotForm";
import { PageLocationList } from "./pages/other/LocationList";
import { PageCompanyList } from "./pages/entities/CompanyList";
import { PageCompanyFormV2 } from "./pages/entities/CompanyFormv2";
import { PageEmployeeList } from "./pages/entities/EmployeeList";
import { PageEmployeeForm } from "./pages/entities/EmployeeForm";
import { PageLocationForm } from "./pages/other/LocationForm";
import { PageInvStatusList } from "./pages/inventory/InvStatusList";
import { PageInvStatusForm } from "./pages/inventory/InvStatusForm";
import { PageInvPosList } from "./pages/inventory/InvPositionList";
import { PageInvMovList } from "./pages/inventory/InvMovementList";
import { PageInvMovForm } from "./pages/inventory/InvMovementForm";
import { PageUOMTypeList } from "./pages/inventory/UOMTypeList";
import { PageUOMTypeForm } from "./pages/inventory/UOMTypeForm";
import { PageUserList } from "./pages/other/UsersList";
import { PageUserForm } from "./pages/other/UsersForm";
import { PagePurchOrderList } from "./pages/purchasing/PurchOrderList";
import { PagePOLineList } from "./pages/purchasing/PurchOrderLinesList";
import { PagePurchOrderForm } from "./pages/purchasing/PurchOrderForm";
import { PageSalesOrderList } from "./pages/sales/SalesOrderList";
import { PageSalesOrderForm } from "./pages/sales/SalesOrderForm";
import { PageWorkOrderList } from "./pages/manufacturing/WorkOrderList";
import { PageWorkOrderForm } from "./pages/manufacturing/WorkOrderForm";
import { PageBOMList } from "./pages/manufacturing/BOMList";
import { PageBOMForm } from "./pages/manufacturing/BOMForm";
import { PageShipmentList } from "./pages/shipments/ShipmentList";
import PageShipmentForm from "./pages/shipments/ShipmentsForm";
import PageInvMovFulfillments from "./pages/inventory/InvMovementFulfillmentList";
import PageDemandForecast from "./pages/mrp/DemandForecaster";
import { PageComplaintList } from "./pages/quality/ComplaintList";
import { QueryClientProvider } from "@tanstack/react-query";
import { ErrorProvider, useError } from "./contexts/ErrorContextManager";
import { setApiErrorHandler } from "./services/api";
import { DevToolsManager } from "./components/DevToolsManager";
import { isDev } from "./config/environment";
import { WorldLocationProvider } from "./contexts/WorldLocationContextProvider";
import { queryClient } from "./lib/queryClient";
import { PagePasswordResetPage } from "./pages/auth/ResetPasswordPage";
import { PageCompleteRegistrationPage } from "./pages/auth/CompleteRegistrationPage";
import PageLowInventory from "./pages/mrp/LowInventory";
import { PageComplaintForm } from "./pages/quality/ComplaintForm";
import { PageRMAList } from "./pages/quality/RMAList";
import { PageBotList } from "./pages/other/BotsList";
import { PageIssueRecordList } from "./pages/quality/IssueTrackerList";
import { ConfirmDialogProvider } from "./contexts/ConfirmDialogProvider";

// const queryClient = new QueryClient();

const ApiErrorHandlerSetup: React.FC = () => {
  const { addError } = useError();
  useEffect(() => {
    setApiErrorHandler(addError);
  }, [addError]);
  return null;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <ErrorProvider>
        <ApiErrorHandlerSetup />
        <QueryClientProvider client={queryClient}>
          <CssBaseline />
          <Router>
            <AuthProvider>
              <Routes>
                {/* ======================================================== */}
                {/* PUBLIC ROUTES — no ProtectedRoute, no AppLayout          */}
                {/* ======================================================== */}
                <Route path="/login" element={<PageLoginPage />} />
                <Route
                  path="/reset-password"
                  element={<PagePasswordResetPage />}
                />
                <Route
                  path="/complete-registration/:token"
                  element={<PageCompleteRegistrationPage />}
                />

                {/* ======================================================== */}
                {/* ROOT REDIRECT — protected, but kept OUTSIDE the layout   */}
                {/* so it doesn't mount AppLayout just to redirect away.     */}
                {/* ======================================================== */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/dashboard" replace />
                    </ProtectedRoute>
                  }
                />

                {/* ======================================================== */}
                {/* PROTECTED LAYOUT ROUTE                                    */}
                {/* Pathless parent: adds nothing to the URL, just wraps its  */}
                {/* children in auth + the confirm provider + AppLayout. Each */}
                {/* child below renders into AppLayout's <Outlet />.          */}
                {/* ======================================================== */}
                <Route
                  element={
                    <ProtectedRoute>
                      <ConfirmDialogProvider>
                        <AppLayout />
                      </ConfirmDialogProvider>
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<PageDashboard />} />

                  {/* Inventory Routes */}
                  <Route path="inventory/items" element={<PageItemList />} />
                  <Route
                    path="inventory/items/form"
                    element={<PageItemForm />}
                  />
                  <Route path="inventory/lots" element={<PageInvLotList />} />
                  <Route
                    path="inventory/lots/form"
                    element={<PageInvLotForm />}
                  />
                  <Route
                    path="/inventory/status"
                    element={<PageInvStatusList />}
                  />
                  <Route
                    path="/inventory/status/form"
                    element={<PageInvStatusForm />}
                  />
                  <Route
                    path="/inventory/balances"
                    element={<PageInvPosList />}
                  />
                  <Route
                    path="/inventory/movements"
                    element={<PageInvMovList />}
                  />
                  <Route
                    path="/inventory/movements/form"
                    element={<PageInvMovForm />}
                  />

                  {/* UOM Type Routes */}
                  <Route path="inventory/uoms" element={<PageUOMTypeList />} />
                  <Route
                    path="inventory/uoms/form"
                    element={<PageUOMTypeForm />}
                  />

                  {/* People / Companies */}
                  <Route path="/people" element={<PageEmployeeList />} />
                  <Route path="/people/form" element={<PageEmployeeForm />} />
                  <Route path="/companies" element={<PageCompanyList />} />
                  <Route
                    path="/companies/form"
                    element={
                      // Per-page provider stays wrapped around just this page.
                      <WorldLocationProvider>
                        <PageCompanyFormV2 />
                      </WorldLocationProvider>
                    }
                  />

                  {/* PO Routes */}
                  <Route
                    path="/orders/purchase"
                    element={<PagePurchOrderList />}
                  />
                  <Route path="/orders/po-lines" element={<PagePOLineList />} />
                  <Route
                    path="/orders/purchase/form"
                    element={
                      <WorldLocationProvider>
                        <PagePurchOrderForm />
                      </WorldLocationProvider>
                    }
                  />

                  {/* SO Routes */}
                  <Route
                    path="/orders/sales"
                    element={<PageSalesOrderList />}
                  />
                  <Route
                    path="/orders/fulfillments"
                    element={<PageInvMovFulfillments />}
                  />
                  <Route
                    path="/orders/sales/form"
                    element={
                      <WorldLocationProvider>
                        <PageSalesOrderForm />
                      </WorldLocationProvider>
                    }
                  />

                  {/* Work Order Routes */}
                  <Route
                    path="/manufacturing/orders"
                    element={<PageWorkOrderList />}
                  />
                  <Route
                    path="/manufacturing/orders/form"
                    element={<PageWorkOrderForm />}
                  />

                  {/* BOM Routes */}
                  <Route path="/manufacturing/boms" element={<PageBOMList />} />
                  <Route
                    path="/manufacturing/boms/form"
                    element={<PageBOMForm />}
                  />

                  {/* Location Routes */}
                  <Route path="/locations" element={<PageLocationList />} />
                  <Route
                    path="/locations/form"
                    element={<PageLocationForm />}
                  />

                  {/* User Routes */}
                  <Route path="/users" element={<PageUserList />} />
                  <Route path="/users/form" element={<PageUserForm />} />

                  {/* Bot Routes */}
                  <Route path="/bot-tokens" element={<PageBotList />} />

                  {/* Shipment Routes */}
                  <Route
                    path="/shipments"
                    element={
                      <WorldLocationProvider>
                        <PageShipmentList />
                      </WorldLocationProvider>
                    }
                  />
                  <Route
                    path="/shipments/form"
                    element={<PageShipmentForm />}
                  />

                  {/* MRP Routes */}
                  <Route
                    path="/mrp/demand_forecasting"
                    element={<PageDemandForecast />}
                  />
                  <Route path="/mrp/low_stock" element={<PageLowInventory />} />

                  {/* Quality Routes */}
                  <Route
                    path="/quality/issue-tracking"
                    element={<PageIssueRecordList />}
                  />
                  <Route
                    path="/quality/customer_complaint"
                    element={<PageComplaintList />}
                  />
                  <Route
                    path="/quality/customer_complaint/form"
                    element={<PageComplaintForm />}
                  />
                  <Route
                    path="/quality/returned_material"
                    element={<PageRMAList />}
                  />
                </Route>
              </Routes>
              <DevToolsManager />
            </AuthProvider>
          </Router>
        </QueryClientProvider>
      </ErrorProvider>
    </ThemeProvider>
  );
}

export default App;
